import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useAccountUserMutations } from '@/hooks/use-account-users';
import { ROLES, type RoleValue } from '../roles';

const schema = z.object({
  mode: z.enum(['new', 'existing']),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email')
    // The control panel provisions Kim staff only; the backend enforces the same rule.
    .refine((value) => value.trim().toLowerCase().endsWith('@kim.cc'), 'Only @kim.cc users can be added'),
  name: z.string().optional(),
  role: z.enum(ROLES.map((r) => r.value) as [RoleValue, ...RoleValue[]])
});

type FormData = z.infer<typeof schema>;

export function AddUserDialog() {
  const { addUser } = useAccountUserMutations();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { mode: 'existing', role: '0' }
  });

  const mode = watch('mode');
  const role = watch('role');

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      const result = await addUser({
        mode: data.mode,
        email: data.email.trim().toLowerCase(),
        name: data.name,
        role: Number(data.role)
      });
      toast.success(
        result?.invited ? 'User added and emailed' : 'User added'
      );
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size='sm'>Add user</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a user to this account</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div className='space-y-1.5'>
            <Label>User type</Label>
            <Select
              value={mode}
              onValueChange={(v) => setValue('mode', v as 'new' | 'existing')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='existing'>Existing user</SelectItem>
                <SelectItem value='new'>New user</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='email'>Email</Label>
            <Input id='email' type='email' {...register('email')} />
            {errors.email && (
              <p className='text-xs text-destructive'>{errors.email.message}</p>
            )}
          </div>
          {mode === 'new' && (
            <div className='space-y-1.5'>
              <Label htmlFor='name'>Name</Label>
              <Input id='name' {...register('name')} />
            </div>
          )}
          <div className='space-y-1.5'>
            <Label>Role</Label>
            <Select
              value={role}
              onValueChange={(v) => setValue('role', v as RoleValue)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type='submit' disabled={saving}>
              {saving ? 'Adding…' : 'Add user'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
